// First-run tenant bootstrap for brand-new accounts.
// If a tenant has no clients yet, we create a default client (1000), validate the
// user's NPI against the national NPPES registry, add the provider, and link it to
// the client. A fake / unregistered NPI never gets added — the NPPES lookup is the gate.
//
// State is persisted client-side only (no local DB), keyed per user, mirroring expMode.js.
import { api } from "@/lib/api";

const KEY = "revq.tenant.bootstrap";
export const DEFAULT_CLIENT_ID = "1000";

const uKey = (user) => String(user?.email || user?.id || "anon").toLowerCase();

export function hasBootstrapped(user) {
  try {
    return !!JSON.parse(localStorage.getItem(KEY) || "{}")[uKey(user)];
  } catch {
    return false;
  }
}

export function markBootstrapped(user) {
  try {
    const m = JSON.parse(localStorage.getItem(KEY) || "{}");
    m[uKey(user)] = new Date().toISOString();
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* storage unavailable — fail quietly */
  }
}

const status = (e) => e?.response?.status;
export const isConflict = (e) => status(e) === 409;
export const isPermissionError = (e) => status(e) === 401 || status(e) === 403;

// NPPES gate: resolve the NPI to a real provider. Throws for a not-found / invalid NPI.
export async function lookupProvider(npi) {
  const { data } = await api.post("/api/edi/note-to-claim/providers/lookup", { npi_number: npi });
  return data;
}

// A resolved lookup must carry real provider identity — otherwise treat as unregistered.
export function isResolvedProvider(data) {
  return !!(data && (data.provider_name || data.npi_number || data.first_name || data.last_name));
}

// Create the default client. Already-exists (409) is treated as success.
export async function ensureDefaultClient(clientId = DEFAULT_CLIENT_ID) {
  try {
    await api.post("/api/edi/note-to-claim/clients", { client_id: clientId });
  } catch (e) {
    if (!isConflict(e)) throw e;
  }
  return clientId;
}

// Add the (NPPES-validated) provider to the tenant. Already-added (409) is success.
export async function addProvider(lookupData) {
  try {
    await api.post("/api/edi/note-to-claim/providers", lookupData);
  } catch (e) {
    if (!isConflict(e)) throw e;
  }
}

export async function linkProviderToClient(npi, clientId) {
  await api.patch(`/api/edi/note-to-claim/providers/${npi}/client`, { client_id: clientId });
}

// Full happy-path: validate → create client → add provider → link.
// Assumes `lookupData` came from a successful, resolved lookupProvider() call.
export async function bootstrapTenant({ npi, lookupData, clientId = DEFAULT_CLIENT_ID }) {
  await ensureDefaultClient(clientId);
  await addProvider(lookupData);
  await linkProviderToClient(npi, clientId);
  return clientId;
}
